import { BsLayoutSplit, BsLightningCharge } from 'react-icons/bs';
import { CgRuler } from 'react-icons/cg';
import { FaCommentAlt, FaRegCommentAlt, FaSort } from 'react-icons/fa';
import {
  FiActivity,
  FiAlertTriangle,
  FiArrowDown,
  FiArrowRight,
  FiArrowUp,
  FiArrowUpRight,
  FiBarChart2,
  FiBookmark,
  FiCalendar,
  FiCheck,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiChevronUp,
  FiClock,
  FiCopy,
  FiDownload,
  FiEdit2,
  FiExternalLink,
  FiEye,
  FiEyeOff,
  FiFile,
  FiFilter,
  FiGrid,
  FiHelpCircle,
  FiLayers,
  FiLink,
  FiList,
  FiMapPin,
  FiMaximize2,
  FiMenu,
  FiMinimize2,
  FiMinus,
  FiMoreHorizontal,
  FiMoreVertical,
  FiPaperclip,
  FiPlus,
  FiPrinter,
  FiRotateCw,
  FiSearch,
  FiSend,
  FiTrash,
  FiUpload,
  FiUser,
  FiX,
  FiZoomIn,
} from 'react-icons/fi';
import { GiFairyWand } from 'react-icons/gi';
import { IoIosHelpCircleOutline } from 'react-icons/io';
import { IoDuplicateOutline } from 'react-icons/io5';
import { LuArrowLeftToLine, LuArrowRightFromLine } from 'react-icons/lu';
import { PiBuildings, PiCurrencyCircleDollar, PiSlidersHorizontal } from 'react-icons/pi';

export type IconName =
  | 'plus'
  | 'minus'
  | 'layers'
  | 'basemap'
  | 'grid'
  | 'upload'
  | 'download'
  | 'file'
  | 'menu'
  | 'check'
  | 'chevronUp'
  | 'chevronDown'
  | 'chevronLeft'
  | 'chevronRight'
  | 'doubleChevronLeft'
  | 'doubleChevronRight'
  | 'close'
  | 'ruler'
  | 'legend'
  | 'delete'
  | 'edit'
  | 'search'
  | 'advancedSearch'
  | 'send'
  | 'visible'
  | 'hidden'
  | 'copy'
  | 'location'
  | 'preview'
  | 'next'
  | 'previous'
  | 'help'
  | 'help2'
  | 'moreHorizontal'
  | 'moreVertical'
  | 'arrowRight'
  | 'arrowUpRight'
  | 'arrowUp'
  | 'arrowDown'
  | 'print'
  | 'user'
  | 'maximize'
  | 'minimize'
  | 'bookmark'
  | 'bookmark-filled'
  | 'lightning'
  | 'zoom'
  | 'paperclip'
  | 'externalLink'
  | 'layoutSplit'
  | 'rotateCw'
  | 'comment'
  | 'commentFilled'
  | 'clock'
  | 'expandTab'
  | 'collapseTab'
  | 'filter'
  | 'activity'
  | 'slider'
  | 'sort'
  | 'calendar'
  | 'warning'
  | 'barChart'
  | 'currency'
  | 'building'
  | 'duplicate'
  | 'link';

export const IconMap: { [Icon in IconName]: JSX.Element } = {
  plus: <FiPlus />,
  minus: <FiMinus />,
  layers: <FiLayers />,
  grid: <FiGrid />,
  basemap: <FiGrid />,
  upload: <FiUpload />,
  download: <FiDownload />,
  file: <FiFile />,
  menu: <FiMenu />,
  check: <FiCheck />,
  calendar: <FiCalendar />,
  chevronUp: <FiChevronUp />,
  chevronDown: <FiChevronDown />,
  chevronLeft: <FiChevronLeft />,
  chevronRight: <FiChevronRight />,
  doubleChevronLeft: <FiChevronsLeft />,
  doubleChevronRight: <FiChevronsRight />,
  close: <FiX />,
  ruler: <CgRuler />,
  legend: <FiList />,
  delete: <FiTrash />,
  edit: <FiEdit2 />,
  search: <FiSearch />,
  advancedSearch: <GiFairyWand />,
  send: <FiSend />,
  visible: <FiEye />,
  hidden: <FiEyeOff />,
  copy: <FiCopy />,
  location: <FiMapPin />,
  preview: <FiEye />,
  next: <FiChevronRight />,
  previous: <FiChevronLeft />,
  help: <FiHelpCircle />,
  help2: <IoIosHelpCircleOutline />,
  moreHorizontal: <FiMoreHorizontal />,
  moreVertical: <FiMoreVertical />,
  arrowUp: <FiArrowUp />,
  arrowDown: <FiArrowDown />,
  arrowRight: <FiArrowRight />,
  arrowUpRight: <FiArrowUpRight />,
  print: <FiPrinter />,
  user: <FiUser />,
  maximize: <FiMaximize2 />,
  minimize: <FiMinimize2 />,
  bookmark: <FiBookmark />,
  'bookmark-filled': <FiBookmark fill={'currentColor'} />,
  lightning: <BsLightningCharge />,
  zoom: <FiZoomIn />,
  paperclip: <FiPaperclip />,
  externalLink: <FiExternalLink />,
  layoutSplit: <BsLayoutSplit strokeWidth={0.5} />,
  rotateCw: <FiRotateCw />,
  comment: <FaRegCommentAlt />,
  commentFilled: <FaCommentAlt />,
  clock: <FiClock />,
  collapseTab: <LuArrowRightFromLine />,
  expandTab: <LuArrowLeftToLine />,
  filter: <FiFilter />,
  activity: <FiActivity />,
  slider: <PiSlidersHorizontal />,
  sort: <FaSort />,
  warning: <FiAlertTriangle />,
  barChart: <FiBarChart2 />,
  currency: <PiCurrencyCircleDollar />,
  building: <PiBuildings />,
  duplicate: <IoDuplicateOutline />,
  link: <FiLink />,
};

interface Props {
  icon: IconName;
  size?: number;
  className?: string;
  ariaLabel?: string;
}

export default function ({ icon, size, className, ariaLabel }: Props) {
  return (
    <span
      className={className}
      style={{ fontSize: size || 14 }}
      {...(ariaLabel ? { role: 'img', 'aria-label': ariaLabel } : { 'aria-hidden': true })}
    >
      {IconMap[icon]}
    </span>
  );
}
